import React, { Component, Fragment } from "react";
import { Table, Button, Card, Row, Col, Tag, Spin, notification, Input, Empty } from "antd";
import { BookOutlined, CheckCircleOutlined, SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import { withRouter } from "react-router";
import { connect } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import { FetchCourse } from "../../Graphs/User/FetchCourse";
import { getDepartments } from "../../Graphs/Department/GetDepartment";
import { AssignCourse } from "../../Graphs/User/AssignCourse";

class AssignDepartmentCourse extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      isSubmitting: false,
      courses: [],
      departments: [],
      selectedCourseId: "",
      selectedDepartmentId: "",
      searchText: "",
      primaryColor: "#4466f2",
    };
  }

  async componentDidMount() {
    const { LoginData, history } = this.props;

    // Restrict access strictly to Corporate Admin logins
    if (LoginData && LoginData.role !== "corporate") {
      notification.error({
        message: "Access Denied",
        description: "This page is strictly accessible to Corporate Admin logins only.",
      });
      history.replace(`${process.env.PUBLIC_URL}/dashboard`);
      return;
    }

    const primaryColor = (await localStorage.getItem("primary_color")) || "#4466f2";
    this.setState({ primaryColor });

    await this.fetchData();
  }

  getAuthToken = () => {
    return this.props.authToken || localStorage.getItem("token") || localStorage.getItem("authToken") || "";
  };

  fetchData = async () => {
    this.setState({ isLoading: true });
    try {
      const token = this.getAuthToken();

      const [courseRes, deptRes] = await Promise.all([
        FetchCourse(token),
        getDepartments(token, 0, 100),
      ]);

      let courses = [];
      if (courseRes && courseRes.data) {
        courses = courseRes.data;
      }

      let departments = [];
      if (deptRes && deptRes.data) {
        departments = deptRes.data;
      }

      this.setState({
        courses,
        departments,
      });
    } catch (error) {
      console.error("Error fetching assign course data:", error);
      notification.error({
        message: "Error",
        description: "Failed to load courses or departments.",
      });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleAssignSubmit = async () => {
    const { selectedCourseId, selectedDepartmentId } = this.state;
    const token = this.getAuthToken();

    if (!selectedCourseId) {
      notification.warning({
        message: "Selection Required",
        description: "Please select a course.",
      });
      return;
    }

    if (!selectedDepartmentId) {
      notification.warning({
        message: "Selection Required",
        description: "Please select a department.",
      });
      return;
    }

    this.setState({ isSubmitting: true });

    try {
      const payload = {
        course_id: selectedCourseId,
        department_ids: [selectedDepartmentId],
      };

      const response = await AssignCourse(token, payload);

      if (response && (response.status === 200 || response.status === 201)) {
        notification.success({
          message: "Success",
          description: "Course assigned successfully!",
        });

        this.setState({
          selectedCourseId: "",
          selectedDepartmentId: "",
        });

        window.location.reload();
      } else {
        notification.error({
          message: "Assignment Failed",
          description: response?.message || "Failed to assign course.",
        });
      }
    } catch (error) {
      console.error("Assign course error:", error);
      notification.error({
        message: "Error",
        description: "An error occurred while assigning course.",
      });
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  render() {
    const {
      isLoading,
      isSubmitting,
      courses,
      departments,
      selectedCourseId,
      selectedDepartmentId,
      searchText,
      primaryColor,
    } = this.state;

    // Filter courses for history table based on search
    const filteredCourses = courses.filter((item) => {
      const courseName = item.courses?.courseDetails?.name || "";
      const depts = item.courses?.assigned_departments || [];
      const deptsString = depts.join(" ");

      return (
        courseName.toLowerCase().includes(searchText.toLowerCase()) ||
        deptsString.toLowerCase().includes(searchText.toLowerCase())
      );
    });

    const columns = [
      {
        title: "Course Name",
        dataIndex: ["courses", "courseDetails", "name"],
        key: "course_name",
        render: (text) => (
          <span style={{ fontWeight: 600, color: "#1f2937" }}>
            <BookOutlined style={{ marginRight: 8, color: primaryColor }} />
            {text || "N/A"}
          </span>
        ),
      },
      {
        title: "Max Users",
        dataIndex: ["courses", "max_user_count"],
        key: "max_users",
        render: (count) => (
          <span className="badge badge-light-primary" style={{ fontSize: "12px", padding: "6px 12px", borderRadius: "20px" }}>
            {count ?? "Unlimited"}
          </span>
        ),
      },
      {
        title: "Assigned Count",
        dataIndex: ["courses", "assign_count"],
        key: "assign_count",
        render: (count) => (
          <span style={{ fontWeight: 500 }}>
            {count || 0}
          </span>
        ),
      },
      {
        title: "Assigned Departments",
        dataIndex: ["courses", "assigned_departments"],
        key: "assigned_departments",
        render: (depts) => {
          if (!depts || depts.length === 0) {
            return <Tag color="default">None Assigned</Tag>;
          }
          return depts.map((dName, idx) => (
            <Tag color="blue" key={idx} style={{ marginBottom: 4, borderRadius: 12, padding: "2px 10px" }}>
              <CheckCircleOutlined style={{ marginRight: 4 }} />
              {dName}
            </Tag>
          ));
        },
      },
    ];

    return (
      <Fragment>
        <Breadcrumb parent="Department" title="Assign Course" />
        <div className="container-fluid" style={{ padding: "0 20px 30px 20px" }}>
          
          {/* Assignment Panel */}
          <Card
            title={<span style={{ fontSize: "16px", fontWeight: 600 }}>Assign Course</span>}
            extra={
              <Button
                icon={<ReloadOutlined />}
                onClick={this.fetchData}
                loading={isLoading}
              >
                Refresh Data
              </Button>
            }
            style={{ borderRadius: "10px", marginBottom: "25px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
          >
            <Row gutter={[20, 20]}>
              {/* Default built-in Course Dropdown */}
              <Col xs={24} md={10}>
                <label style={{ fontWeight: 600, marginBottom: 8, display: "block", color: "#374151" }}>
                  Select Course <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <select
                  id="select_course"
                  className="form-control"
                  value={selectedCourseId}
                  onChange={(e) => this.setState({ selectedCourseId: e.target.value })}
                  style={{
                    width: "100%",
                    cursor: "pointer",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    padding: "6px 12px",
                    height: "38px",
                    outline: "none",
                    fontSize: "14px",
                    color: "#374151",
                  }}
                >
                  <option value="">Select Course</option>
                  {courses &&
                    courses.map((item) => {
                      const cDetails = item.courses?.courseDetails;
                      if (!cDetails) return null;
                      return (
                        <option key={cDetails._id} value={cDetails._id}>
                          {cDetails.name}
                        </option>
                      );
                    })}
                </select>
              </Col>

              {/* Default built-in Department Dropdown */}
              <Col xs={24} md={10}>
                <label style={{ fontWeight: 600, marginBottom: 8, display: "block", color: "#374151" }}>
                  Select Department <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <select
                  id="select_department"
                  className="form-control"
                  value={selectedDepartmentId}
                  onChange={(e) => this.setState({ selectedDepartmentId: e.target.value })}
                  style={{
                    width: "100%",
                    cursor: "pointer",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    padding: "6px 12px",
                    height: "38px",
                    outline: "none",
                    fontSize: "14px",
                    color: "#374151",
                  }}
                >
                  <option value="">Select Department</option>
                  {departments &&
                    departments.map((dept) => (
                      <option key={dept._id} value={dept._id}>
                        {dept.name}
                      </option>
                    ))}
                </select>
              </Col>

              <Col xs={24} md={4} style={{ display: "flex", alignItems: "flex-end" }}>
                <Button
                  type="primary"
                  block
                  loading={isSubmitting}
                  onClick={this.handleAssignSubmit}
                  style={{
                    backgroundColor: primaryColor,
                    borderColor: primaryColor,
                    height: "38px",
                    borderRadius: "6px",
                    fontWeight: 600,
                  }}
                >
                  Assign Course
                </Button>
              </Col>
            </Row>
          </Card>

          {/* History & Assigned Status Table */}
          <Card
            title={
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                <span style={{ fontSize: "16px", fontWeight: 600 }}>Department Course Assignment History</span>
                <Input
                  prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                  placeholder="Search course or department..."
                  value={searchText}
                  onChange={(e) => this.setState({ searchText: e.target.value })}
                  style={{ width: 280, borderRadius: 20 }}
                  allowClear
                />
              </div>
            }
            style={{ borderRadius: "10px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
          >
            {isLoading ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <Spin size="large" />
                <p style={{ marginTop: 12, color: "#6b7280" }}>Loading assigned course history...</p>
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={filteredCourses}
                rowKey={(record) => record.courses?.courseDetails?._id || Math.random().toString()}
                pagination={{ pageSize: 10, showSizeChanger: true }}
                locale={{ emptyText: <Empty description="No department course assignments found" /> }}
              />
            )}
          </Card>

        </div>
      </Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  authToken: state.auth.authToken,
  LoginData: state.auth.loginUserData,
});

export default connect(mapStateToProps)(withRouter(AssignDepartmentCourse));
